import React, { useRef, useState } from 'react';

import useOnClickOutside from 'hooks/useOnClickOutside';

import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import Box from 'components/elements/Box';
import Span from 'components/elements/span';
import P from 'components/elements/p';
import CountryFlag from 'components/base/CountryFlag';
import { StyledCaret } from 'components/Charts/ChartTable/chartTable.style';
import { Button } from 'components/TopicFilters/TopicFilters.styles';
import { BoxShadowed } from './styles';

import { ReactComponent as RegionInfoIcon } from 'icons/icon-info.svg';

import colors from 'styles/themes/colors';

import { ICountryFlagProps } from 'types/shared.d';

interface ICountry extends ICountryFlagProps {
  label: string;
  value: string;
}

export type Region = {
  countries: ICountryFlagProps[];
  displayName: string;
  id: string;
  label?: string;
  value?: string;
};

const getCountriesList = (countries: ICountryFlagProps[]): string =>
  (countries || [])
    .reduce(
      (countries: string[], country: ICountryFlagProps) => [
        ...countries,
        country.displayName,
      ],
      []
    )
    .sort()
    .join('\n');

const Country: any = ({
  country,
  onChange,
  py,
  saveLocation,
  setLocation,
  setActive,
}: {
  country: ICountryFlagProps;
  onChange: (c: ICountryFlagProps) => void;
  py: any;
  saveLocation?: (location: string, id: string) => void;
  setLocation: (c: ICountryFlagProps) => void;
  setActive?: (isActive: boolean) => void;
}) => (
  <BoxShadowed>
    <P
      px={5}
      py={py}
      onClick={(): void => {
        onChange(country);
        setLocation(country);
        setActive && setActive(false);
        saveLocation && saveLocation('country', country.id);
      }}
      style={{ cursor: 'pointer' }}
    >
      {country?.displayName}
    </P>
  </BoxShadowed>
);

const CountriesList = ({
  countries,
  isActive,
  onChange,
  saveLocation,
  setActive,
  setLocation,
}: {
  countries: ICountry[];
  isActive: boolean;
  onChange: (c: ICountryFlagProps) => void;
  saveLocation?: (location: 'country' | 'region', id: string) => void;
  setLocation: (c: ICountryFlagProps) => void;
  setActive: (isActive: boolean) => any;
}): JSX.Element => (
  <Box>
    <Box
      onClick={(e: any): void => {
        e.stopPropagation();
        setActive(!isActive);
      }}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      style={{ cursor: 'default' }}
      px={5}
      py={4}
    >
      <span>Select Territory</span>
      <StyledCaret open={isActive} />
    </Box>

    {isActive && (
      <Box maxHeight="300px" overflowY="auto">
        {countries.map((country: ICountryFlagProps, index: number) => (
          <Country
            key={index}
            country={country}
            onChange={onChange}
            py={2}
            saveLocation={saveLocation}
            setActive={setActive}
            setLocation={setLocation}
          />
        ))}
      </Box>
    )}
  </Box>
);

const RegionItem: any = ({
  countries,
  isLargeScreen,
  onChange,
  py,
  region,
  saveLocation,
  setLocation,
  setActive,
}: {
  countries: ICountryFlagProps[];
  isLargeScreen: boolean;
  py: any;
  region: Region;
  onChange: (c: any) => void;
  saveLocation?: (location: 'country' | 'region', id: string) => void;
  setLocation: (c: any) => void;
  setActive?: (isActive: boolean) => void;
}) => {
  return (
    <BoxShadowed
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      px={5}
      style={{ cursor: 'pointer' }}
      onClick={(): void => {
        onChange(region);
        setLocation(region);
        setActive && setActive(false);
        saveLocation && saveLocation('region', region.id);
      }}
    >
      <P py={py}>{region?.displayName}</P>

      {isLargeScreen && countries.length > 0 && (
        <TippyToolTip
          tooltipContent={countries}
          enabled
          placement="right"
          zIndex="10001"
        >
          <Box alignSelf="auto" display="flex" ml={3}>
            <RegionInfoIcon display="inline-flex" width={16} height={16} />
          </Box>
        </TippyToolTip>
      )}
    </BoxShadowed>
  );
};

const RegionsList = ({
  isActive,
  isLargeScreen,
  onChange,
  regions,
  saveLocation,
  setActive,
  setLocation,
}: {
  isActive: boolean;
  isLargeScreen: boolean;
  regions: Region[];
  onChange: (c: ICountryFlagProps) => void;
  saveLocation?: (location: 'country' | 'region', id: string) => void;
  setLocation: (c: ICountryFlagProps) => void;
  setActive: (isActive: boolean) => void;
}): JSX.Element => (
  <Box borderTop="1px solid #e9e9e9">
    <Box
      onClick={(e: any): void => {
        e.stopPropagation();
        setActive(!isActive);
      }}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      style={{ cursor: 'default' }}
      px={5}
      py={4}
    >
      <span>Select Region</span>
      <StyledCaret open={isActive} />
    </Box>

    {isActive && (
      <Box maxHeight="300px" overflowY="auto">
        {regions.map((region: any, index: number) => (
          <RegionItem
            key={index}
            countries={getCountriesList(region?.countries)}
            isLargeScreen={isLargeScreen}
            onChange={onChange}
            py={2}
            region={region}
            saveLocation={saveLocation}
            setLocation={setLocation}
            setActive={setActive}
          />
        ))}
      </Box>
    )}
  </Box>
);

const RecentLocations: any = ({
  isLargeScreen,
  onChange,
  recentCountries,
  recentRegions,
  setLocation,
}: {
  isLargeScreen: boolean;
  recentCountries: ICountryFlagProps[];
  recentRegions: Region[];
  onChange: (c: ICountryFlagProps) => void;
  setLocation: (c: ICountryFlagProps) => void;
}) => {
  return (
    <Box py={12} borderBottom="1px solid #e9e9e9">
      <P pl={5} color={colors.CasperGrey} fontSize={11} fontWeight={600}>
        RECENT
      </P>

      {recentCountries.map((country: any, index: number) => (
        <Country
          key={index}
          country={country}
          py="5px"
          onChange={onChange}
          setLocation={setLocation}
        />
      ))}

      {recentRegions.map((region: any, index: number) => (
        <RegionItem
          key={index}
          countries={getCountriesList(region?.countries)}
          isLargeScreen={isLargeScreen}
          onChange={onChange}
          py="5px"
          region={region}
          setLocation={setLocation}
        />
      ))}
    </Box>
  );
};

const LocationDropdown: React.FC<{
  clearLocation: (location: string) => void;
  isLargeScreen: boolean;
  onChange: (c: ICountryFlagProps) => void;
  saveLocation?: (location: 'country' | 'region', id: string) => void;
  sortedCountries: ICountry[];
  sortedRegions: Region[];
  recentCountries: (ICountryFlagProps | undefined)[];
  recentRegions: (Region | undefined)[];
}> = ({
  clearLocation,
  isLargeScreen,
  onChange,
  saveLocation,
  sortedCountries,
  sortedRegions,
  recentCountries = [],
  recentRegions = [],
}) => {
  const [isOpened, openDropdown] = useState(false);
  const [isCountryActive, setCountryActive] = useState(false);
  const [isRegionActive, setRegionActive] = useState(false);
  const [selectedLocation, setLocation] = useState<any>();
  const menuRootRef = useRef(null);

  useOnClickOutside(menuRootRef, () => {
    if (isOpened) {
      openDropdown(false);
    }
  });

  return (
    <Box
      position="relative"
      onClick={(): void => openDropdown(!isOpened)}
      ref={menuRootRef}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        py={isLargeScreen ? 1 : 3}
        px={isLargeScreen ? 2 : 4}
        backgroundColor={colors.White}
      >
        <Span fontWeight="600" maxWidth="160px">
          {(selectedLocation && selectedLocation?.displayName) || 'Global'}
        </Span>
        <StyledCaret open={isOpened} />
      </Box>

      {isOpened && (
        <Box
          position="absolute"
          width={isLargeScreen ? '265px' : '100%'}
          backgroundColor={colors.White}
          boxShadow="0px 4px 4px rgba(0, 0, 0, 0.06)"
          top="calc(100% + 2px)"
          maxHeight="500px"
          overflowY="auto"
          zIndex={2}
        >
          <RecentLocations
            isLargeScreen={isLargeScreen}
            recentCountries={recentCountries}
            recentRegions={recentRegions}
            onChange={onChange}
            setLocation={setLocation}
          />

          <Box
            py={3}
            px={5}
            style={{ cursor: 'pointer' }}
            onClick={(): void => {
              selectedLocation &&
                clearLocation(
                  selectedLocation.id.includes('RE-') ? 'region' : 'country'
                );
              setLocation({ value: 'global', displayName: 'Global' });
              setCountryActive(false);
              setRegionActive(false);
            }}
          >
            <CountryFlag
              small
              id="WW"
              displayName="Global"
              flagImgSmall=""
              flagImgMedium=""
              flagImgLarge=""
            />
            <Span alignSelf="auto">Global</Span>
          </Box>

          <CountriesList
            countries={sortedCountries}
            isActive={isCountryActive}
            onChange={onChange}
            saveLocation={saveLocation}
            setActive={setCountryActive}
            setLocation={setLocation}
          />

          <RegionsList
            isActive={isRegionActive}
            isLargeScreen={isLargeScreen}
            onChange={onChange}
            regions={sortedRegions}
            saveLocation={saveLocation}
            setActive={setRegionActive}
            setLocation={setLocation}
          />

          <Button
            borderColor={'LightGrey'}
            borderWidth={'1px'}
            borderStyle={'solid'}
            py={4}
            isActive={false}
            onClick={(): void => {
              if (selectedLocation) {
                selectedLocation.hasOwnProperty('id')
                  ? clearLocation(
                      selectedLocation.id.includes('RE-') ? 'region' : 'country'
                    )
                  : setLocation(undefined);
              }
              setLocation(undefined);
              setCountryActive(false);
              setRegionActive(false);
            }}
          >
            Clear this filter
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default LocationDropdown;
